import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { API } from "@/lib/api";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { RatingStars } from "@/components/RatingStars";
import { BrandLogo } from "@/components/BrandLogo";
import { BadgeCheck, MapPin, CheckCircle2, Calendar, ArrowLeft, Building2, Home, Globe, ShieldCheck } from "lucide-react";

export default function RemovalistPublicProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/removalists/${id}`).then(({ data }) => setP(data)).catch(() => setP(null)).finally(() => setLoading(false));
  }, [id]);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/90 backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <BrandLogo />
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate(-1)} className="gap-1"><ArrowLeft className="h-4 w-4" /> Back</Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="nav-home-btn" className="gap-1"><Home className="h-4 w-4" /> Home</Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading && <p className="text-muted-foreground">Loading profile…</p>}
        {!loading && !p && <p className="text-muted-foreground">Removalist not found.</p>}
        {p && (
          <>
            <div className="flex flex-wrap items-start gap-6 mb-8">
              {p.has_logo ? (
                <div className="h-20 w-20 rounded-xl border border-border bg-white overflow-hidden flex items-center justify-center" data-testid="public-logo">
                  <img src={`${API}/public/logo/${p.id}`} alt={`${p.company_name || p.name} logo`} className="h-full w-full object-contain" />
                </div>
              ) : (
                <Avatar className="h-20 w-20 border border-border">
                  <AvatarFallback className="bg-asphalt text-white text-2xl font-bold">{(p.company_name || p.name)?.slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
              )}
              <div className="flex-1 min-w-[240px]">
                <h1 className="font-display text-3xl font-bold flex items-center gap-2">
                  {p.company_name || p.name}
                  {p.is_verified && <BadgeCheck className="h-6 w-6 text-emerald-500" />}
                </h1>
                <div className="mt-2"><RatingStars value={p.rating || 0} count={p.rating_count ?? 0} size={18} /></div>
                {p.abn_or_license && <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1"><Building2 className="h-4 w-4" /> {p.abn_or_license}</p>}
                {p.website && <a href={p.website} target="_blank" rel="noopener noreferrer" data-testid="public-website-link" className="text-sm text-kinetic hover:underline mt-1 flex items-center gap-1"><Globe className="h-4 w-4" /> {p.website.replace(/^https?:\/\//, "")}</a>}
                {p.insurance && <p className="text-sm mt-1 flex items-center gap-1" data-testid="public-insurance"><ShieldCheck className="h-4 w-4 text-emerald-500" /> <span className="text-muted-foreground">Insured:</span> {p.insurance}</p>}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-4 text-center min-w-[110px]"><p className="font-display text-2xl font-bold text-emerald-600">{p.completed_jobs}</p><p className="text-xs text-muted-foreground uppercase font-mono">Jobs done</p></Card>
                <Card className="p-4 text-center min-w-[110px]"><p className="font-display text-2xl font-bold">{p.rating_count}</p><p className="text-xs text-muted-foreground uppercase font-mono">Reviews</p></Card>
              </div>
            </div>

            {(p.coverage?.length > 0 || p.service_zones?.length > 0) && (
              <Card className="p-6 mb-6">
                <h2 className="font-display font-semibold text-lg flex items-center gap-2 mb-3"><MapPin className="h-5 w-5 text-kinetic" /> Service areas</h2>
                <div className="flex flex-wrap gap-2">
                  {p.coverage?.map((c, i) => (
                    <Badge key={i} variant="outline" className="bg-kinetic-light border-kinetic/30 text-asphalt py-1.5 px-3">
                      {c.suburb}{c.state ? `, ${c.state}` : ""}{c.postcode ? ` ${c.postcode}` : ""}
                    </Badge>
                  ))}
                  {(!p.coverage || p.coverage.length === 0) && p.service_zones?.map((z) => (
                    <Badge key={z} variant="outline" className="bg-kinetic-light border-kinetic/30 text-asphalt py-1.5 px-3">{z}</Badge>
                  ))}
                </div>
              </Card>
            )}

            <Card className="p-6">
              <h2 className="font-display font-semibold text-lg mb-4">Customer reviews</h2>
              {p.reviews.length === 0 && <p className="text-sm text-muted-foreground">No reviews yet.</p>}
              <div className="space-y-4">
                {p.reviews.map((r) => (
                  <div key={r.id} className="border-b border-border/60 pb-4 last:border-0">
                    <div className="flex items-center justify-between">
                      <RatingStars value={r.rating} size={14} />
                      <span className="text-xs text-muted-foreground flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                    {r.comment && <p className="text-sm mt-2">{r.comment}</p>}
                    <p className="text-xs text-muted-foreground mt-1">— {r.customer_name}{r.job_title ? ` · ${r.job_title}` : ""}</p>
                  </div>
                ))}
              </div>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
